/**
 * Employer Navigation Page
 *
 * AI and human navigation assistance for employees to find the right benefits
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Compass,
  MessageSquare,
  Phone,
  Video,
  Bot,
  User,
  Send,
  Clock,
  CheckCircle,
  ArrowRight,
  HelpCircle,
} from 'lucide-react';
import EmployerLayout from '../layout';

interface Message {
  id: number;
  type: 'user' | 'ai' | 'system';
  content: string;
  timestamp: Date;
}

export default function EmployerNavigationPage() {
  const { t } = useTranslation('employer');

  const quickTopics = [
    { id: 'benefits', label: t('navigation.topicBenefits'), icon: HelpCircle },
    { id: 'enroll', label: t('navigation.topicEnroll'), icon: CheckCircle },
    { id: 'leave', label: t('navigation.topicLeave'), icon: Clock },
    { id: 'wellness', label: t('navigation.topicWellness'), icon: Compass },
  ];

  const [messages, setMessages] = useState<Message[]>([
    {
      id: 1,
      type: 'ai',
      content: "Hello! I'm your benefits navigator. I can help you find the right programs, understand your benefits, or connect you with a human navigator. What would you like help with today?",
      timestamp: new Date(),
    },
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [sessionType, setSessionType] = useState<'ai' | 'human' | null>(null);

  const handleSendMessage = () => {
    if (!inputMessage.trim()) return;

    const userMessage: Message = {
      id: messages.length + 1,
      type: 'user',
      content: inputMessage,
      timestamp: new Date(),
    };

    setMessages([...messages, userMessage]);
    setInputMessage('');

    // Simulate AI response
    setTimeout(() => {
      const aiResponse: Message = {
        id: messages.length + 2,
        type: 'ai',
        content: "I understand you're asking about that. Let me help you find the right information. Based on your question, I'd recommend checking out our Employee Assistance Program (EAP) which provides confidential support. Would you like me to tell you more about it or help you enroll?",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, aiResponse]);
    }, 1000);
  };

  const handleQuickTopic = (topic: string) => {
    const topicMessages: Record<string, string> = {
      benefits: "Can you help me understand my benefits?",
      enroll: "How do I enroll in a benefit program?",
      leave: "I need information about taking leave",
      wellness: "What wellness resources are available?",
    };

    setInputMessage(topicMessages[topic] || topic);
  };

  const startHumanSession = () => {
    setSessionType('human');
    const systemMessage: Message = {
      id: messages.length + 1,
      type: 'system',
      content: t('navigation.connectingHuman') + " A navigator will join this chat shortly. Average wait time is 2-3 minutes.",
      timestamp: new Date(),
    };
    setMessages([...messages, systemMessage]);
  };

  return (
    <EmployerLayout>
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden h-[calc(100vh-200px)] flex flex-col">
          {/* Header */}
          <div className="bg-gradient-to-r from-green-600 to-teal-600 px-6 py-4 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Compass className="w-8 h-8" />
                <div>
                  <h1 className="text-xl font-bold">{t('navigation.title')}</h1>
                  <p className="text-green-100 text-sm">{t('navigation.subtitle')}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="px-3 py-1 bg-white/20 rounded-full text-sm flex items-center">
                  <Bot className="w-4 h-4 mr-1" />
                  {t('navigation.aiMode')}
                </span>
              </div>
            </div>
          </div>

          {/* Quick Topics */}
          {messages.length === 1 && (
            <div className="p-4 border-b border-gray-200 bg-gray-50">
              <p className="text-sm text-gray-600 mb-3">{t('navigation.quickTopics')}</p>
              <div className="flex flex-wrap gap-2">
                {quickTopics.map((topic) => (
                  <button
                    key={topic.id}
                    onClick={() => handleQuickTopic(topic.id)}
                    className="inline-flex items-center px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-700 hover:bg-gray-50 hover:border-green-300 transition-colors"
                  >
                    <topic.icon className="w-4 h-4 mr-2 text-green-600" />
                    {topic.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                    message.type === 'user'
                      ? 'bg-green-600 text-white rounded-br-md'
                      : message.type === 'system'
                      ? 'bg-yellow-50 text-yellow-800 border border-yellow-200'
                      : 'bg-gray-100 text-gray-900 rounded-bl-md'
                  }`}
                >
                  {message.type === 'ai' && (
                    <div className="flex items-center gap-2 mb-1">
                      <Bot className="w-4 h-4 text-green-600" />
                      <span className="text-xs font-medium text-green-600">{t('navigation.navigatorAI')}</span>
                    </div>
                  )}
                  <p className="text-sm">{message.content}</p>
                  <p className={`text-xs mt-1 ${message.type === 'user' ? 'text-green-200' : 'text-gray-500'}`}>
                    {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Human Navigator Option */}
          {!sessionType && (
            <div className="px-4 py-3 bg-blue-50 border-t border-blue-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <User className="w-5 h-5 text-blue-600" />
                  <span className="text-sm text-blue-900">{t('navigation.needHumanAssistance')}</span>
                </div>
                <button
                  onClick={startHumanSession}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
                >
                  {t('navigation.connectWithNavigator')}
                </button>
              </div>
            </div>
          )}

          {/* Input */}
          <div className="p-4 border-t border-gray-200 bg-white">
            <div className="flex items-center gap-3">
              <input
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                placeholder={t('navigation.typeQuestion')}
                className="flex-1 px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500"
              />
              <button
                onClick={handleSendMessage}
                disabled={!inputMessage.trim()}
                className="p-3 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </EmployerLayout>
  );
}
